/**
 * ERROS DE TELA (rodada FUNDAÇÕES, A1 — 17/08/2026) — a tela de admin.
 *
 * O que quebrou pra alguém aparece AQUI, sem depender de aviso: erros de JS,
 * promessas rejeitadas e falhas de render, agrupados por fingerprint (cem
 * ocorrências do mesmo erro = uma linha). Resumo de 24h no topo; "novo" marca
 * grupo que estreou nas últimas 24h; falhas de rede do usuário são marcadas
 * à parte pra não virar ruído.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  Badge,
  Card,
  EmptyState,
  HelpTip,
  Input,
  PageHeader,
  Select,
  SelectOption,
} from '@/components/ui';
import { api } from '@/lib/api';

import { formatarData } from '@/lib/format-date';
interface ErroDeTela {
  fingerprint: string;
  tipo: 'js' | 'promessa' | 'render' | 'rede';
  mensagem: string;
  stack: string | null;
  rota: string;
  perfil: string | null;
  navegador: string | null;
  ocorrencias: number;
  primeiro_em_iso: string;
  ultimo_em_iso: string;
}

interface Resp {
  items: ErroDeTela[];
  resumo: { grupos: number; total: number; ultimas24h: number; grupos_novos_24h: number };
}

const TIPO_LABEL: Record<ErroDeTela['tipo'], string> = {
  js: 'Erro de tela',
  promessa: 'Operação falhou',
  render: 'Falha ao montar a tela',
  rede: 'Falha de rede (do usuário)',
};

const PERIODOS = [
  { key: '1', label: 'Últimas 24 horas' },
  { key: '7', label: 'Últimos 7 dias' },
  { key: '30', label: 'Últimos 30 dias' },
  { key: '', label: 'Tudo' },
] as const;

function fmtBrt(iso: string): string {
  return formatarData(new Date(iso), {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ErrosDeTelaPage() {
  const [rota, setRota] = useState('');
  const [periodo, setPeriodo] = useState<string>('7');
  const [aberto, setAberto] = useState<string | null>(null);

  const desde = useMemo(() => {
    if (!periodo) return undefined;
    return new Date(Date.now() - Number(periodo) * 24 * 60 * 60 * 1000).toISOString();
  }, [periodo]);

  const { data, isLoading } = useQuery<Resp>({
    queryKey: ['erros-de-tela', rota, desde],
    queryFn: () => {
      const q = new URLSearchParams();
      if (rota.trim()) q.set('rota', rota.trim());
      if (desde) q.set('desde', desde);
      return api.get<Resp>(`/api/v1/admin/erros-de-tela?${q}`);
    },
    refetchInterval: 60_000,
  });

  const agora24h = Date.now() - 24 * 60 * 60 * 1000;

  return (
    <section className="min-w-0 space-y-4" data-testid="erros-de-tela-page">
      <PageHeader
        className="mb-0"
        crumb="Configurações · Sistema"
        title="Erros de tela"
        subtitle="O que quebrou pra alguém, registrado sozinho — sem depender de aviso."
        actions={
          <HelpTip texto="Quando a tela de alguém quebra, o registro chega aqui automaticamente: a mensagem técnica, a tela, o papel de quem estava logado (nunca o nome) e o navegador. Erros iguais são agrupados numa linha só. Nenhum dado digitado nas telas é gravado. 'Falha de rede (do usuário)' é a internet de quem usava, não defeito do sistema." />
        }
      />

      {/* O resumo que se olha todo dia: quantos nas últimas 24h. */}
      {data && (
        <Card className="p-4 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="erros-resumo">
          <span className="text-sm">
            <strong
              className={
                data.resumo.ultimas24h > 0 ? 'text-danger text-lg' : 'text-ink-1 dark:text-ink-dark-1 text-lg'
              }
            >
              {data.resumo.ultimas24h}
            </strong>{' '}
            erro{data.resumo.ultimas24h === 1 ? '' : 's'} nas últimas 24h
          </span>
          {data.resumo.grupos_novos_24h > 0 && (
            <Badge variant="danger" size="sm">
              {data.resumo.grupos_novos_24h} tipo{data.resumo.grupos_novos_24h === 1 ? '' : 's'} novo
              {data.resumo.grupos_novos_24h === 1 ? '' : 's'} nas 24h
            </Badge>
          )}
          <span className="text-xs text-ink-3 dark:text-ink-dark-3">
            {data.resumo.grupos} tipo{data.resumo.grupos === 1 ? '' : 's'} de erro ·{' '}
            {data.resumo.total} ocorrência{data.resumo.total === 1 ? '' : 's'} no total
          </span>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input
          size="sm"
          value={rota}
          onChange={(e) => setRota(e.target.value)}
          placeholder="Filtrar pela tela (ex.: /admin/comercial)"
          aria-label="Filtrar pela tela"
          className="max-w-xs"
          data-testid="erros-filtro-rota"
        />
        <Select
          size="sm"
          value={periodo}
          onChange={(e) => setPeriodo(e.target.value)}
          aria-label="Período"
          data-testid="erros-filtro-periodo"
        >
          {PERIODOS.map((p) => (
            <SelectOption key={p.key} value={p.key}>
              {p.label}
            </SelectOption>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 italic">Carregando…</p>
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          title="Nenhum erro registrado no período"
          description="Ótimo sinal — nenhuma tela quebrou. O registro só cria linha quando algo falha de verdade."
        />
      ) : (
        <div className="space-y-2" data-testid="erros-lista">
          {data.items.map((e) => {
            const novo = new Date(e.primeiro_em_iso).getTime() >= agora24h;
            const expandido = aberto === e.fingerprint;
            return (
              <Card
                key={e.fingerprint}
                className="p-3 cursor-pointer"
                onClick={() => setAberto(expandido ? null : e.fingerprint)}
                data-testid="erro-linha"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant={e.tipo === 'rede' ? 'neutral' : 'danger'} size="sm">
                        {TIPO_LABEL[e.tipo]}
                      </Badge>
                      {novo && (
                        <Badge variant="warning" size="sm">
                          novo
                        </Badge>
                      )}
                      <span className="text-xs text-ink-3 dark:text-ink-dark-3">{e.rota}</span>
                    </div>
                    <p className="text-sm text-ink-1 dark:text-ink-dark-1 mt-1 break-words">
                      {e.mensagem}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                      {e.ocorrencias}×
                    </p>
                    <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                      {fmtBrt(e.primeiro_em_iso)} → {fmtBrt(e.ultimo_em_iso)}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                  {e.perfil ? `perfil: ${e.perfil}` : ''}
                  {e.navegador ? ` · ${e.navegador}` : ''}
                </p>
                {expandido && e.stack && (
                  <pre className="mt-2 text-xs bg-paper-warm dark:bg-paper-dark-warm rounded-lg p-2 overflow-x-auto max-h-64">
                    {e.stack}
                  </pre>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
}
